import React from 'react';
import { AriaProgressBarProps, useProgressBar } from 'react-aria';
import { useTheme } from 'styled-components';
import { Heading } from 'components/Typography';
import * as S from './styles';

export interface ProgressRingProps extends AriaProgressBarProps {
  className?: string;
  fillColor?: string;
  fillWidth?: number;
  hideLabel?: boolean;
  ringClassName?: string;
  trackColor?: string;
  trackWidth?: number;
}

const ProgressRing = React.forwardRef<HTMLDivElement, ProgressRingProps>(
  (
    {
      className,
      fillColor,
      fillWidth = 5,
      hideLabel,
      ringClassName,
      trackColor,
      trackWidth = 3,
      ...props
    },
    ref,
  ) => {
    const { isIndeterminate, value } = props;
    const { labelProps, progressBarProps } = useProgressBar(props);
    const theme = useTheme();

    const min = Number(progressBarProps['aria-valuemin']);
    const max = Number(progressBarProps['aria-valuemax']);

    const currentValue = value ?? min;

    return (
      <S.Root className={className} {...progressBarProps} ref={ref}>
        <S.Ring
          $isIndeterminate={isIndeterminate}
          className={ringClassName}
          percent={isIndeterminate ? 25 : (currentValue / max) * 100}
          strokeColor={fillColor || theme.palette.actionPrimary.main}
          strokeWidth={fillWidth}
          trailColor={trackColor || '#464e5e'}
          trailWidth={trackWidth}
        />
        {!isIndeterminate && !hideLabel && (
          // an svg is used to render the text so that it can easily be consrained by
          // the size of the circle progress that it is rendered within.  percentage
          // font sizes are based on parent font sizes, but we need something based
          // on the parent container size
          <S.LabelContainer
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            {...labelProps}
          >
            <Heading
              as="text"
              level={1}
              x="55%"
              y="50%"
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {value}%
            </Heading>
          </S.LabelContainer>
        )}
      </S.Root>
    );
  },
);

export default ProgressRing;
